import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MicIcon from '@mui/icons-material/Mic';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import TranslateIcon from '@mui/icons-material/Translate';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import api from '../../../../api/client';
import B2CInterviewSetupModal from '../../../modals/B2CInterviewSetupModal';
import { useCandidateData } from '../../../../hooks/useCandidateData';
import TransactionHistory from '../../../user/TransactionHistory';
import targetImg from '../../../../assets/images/target.png';
import ViewResult from '../../component/ViewResult';
import PageHeader, { PageCard, PageShell } from '../../../common/PageHeader';

const THEME_GRADIENT = 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)';
const THEME_BUTTON_SHADOW = '0 8px 18px -10px rgba(217, 70, 239, 0.85)';
const getLowCreditThreshold = () => {
  const envVal = process.env.REACT_APP_LOW_CREDIT_THRESHOLD;
  const parsed = parseInt(envVal, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
};

const isCreditLow = (balance) => {
  if (balance === undefined || balance === null) return false;
  return Number(balance) <= getLowCreditThreshold();
};

const getNormalizedCredits = (data) => {
  const payload = data?.results || data || {};
  const primaryCredits = [
    payload.available_credits,
    payload.availableCredits,
    payload.remaining_credits,
    payload.remainingCredits,
    payload.balance,
  ]
    .map((value) => Number(value))
    .find((value) => Number.isFinite(value));

  if (Number.isFinite(primaryCredits)) {
    return primaryCredits;
  }

  const currentBalance = Number(payload.current_balance ?? payload.currentBalance);
  const dailyCredits = Number(payload.daily_credits ?? payload.dailyCredits);

  return (Number.isFinite(currentBalance) ? currentBalance : 0) +
    (Number.isFinite(dailyCredits) ? dailyCredits : 0);
};

const formatExpiry = (dateStr, lng = 'en') => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString(lng, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  }
};

const CountdownDisplay = ({ dateStr, compact = false }) => {
  const [timeLeft, setTimeLeft] = useState('');
  
  useEffect(() => {
    if (!dateStr) return;
    
    const calculateTimeLeft = () => {
      const difference = new Date(dateStr) - new Date();
      if (difference <= 0) {
        setTimeLeft('Expired');
        return;
      }
      
      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference / 1000 / 60) % 60);
      setTimeLeft(compact ? `${hours}h ${minutes}m` : `Expires in ${hours}h ${minutes}m`);
    };
    
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 60000);
    
    return () => clearInterval(timer);
  }, [dateStr, compact]);
  
  if (!dateStr) return <span className="text-gray-400 font-normal">N/A</span>;
  if (timeLeft === 'Expired') return <span className="text-red-500 font-bold">Expired</span>;
  return <span>{timeLeft}</span>;
};

const getTranslatedStatus = (status, t) => {
  if (!status) return t('b2cDashboard.status.unknown', 'Unknown');
  if (status === 'completed') return t('b2cDashboard.status.completed', 'Completed');
  if (status === 'in_progress') return t('b2cDashboard.status.inProgress', 'In Progress');
  if (status === 'pending') return t('b2cDashboard.status.pending', 'Pending');

  return status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const getInterviewScore = (interview) => {
  const effectiveScore = interview?.feedback?.detailed_breakdown?.effective_score;
  const totalScore = interview?.feedback?.total_score ?? interview?.total_score;
  const score = effectiveScore ?? totalScore;

  return Number.isFinite(Number(score)) ? Math.round(Number(score)) : null;
};



// ──────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────
export default function CandidateDashboard() {
  const navigate = useNavigate();
  const { candidateData } = useCandidateData();
  const { t, i18n } = useTranslation();
  const [creditData, setCreditData] = useState({
    current_balance: null,
    current_balance_expiry: null,
    daily_credits: 0,
    daily_credits_expiry: null,
    lifetime_credits: 0,
  });
  const [, setCreditLoading] = useState(true);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [selectedResume, setSelectedResume] = useState(null);
  const [startingInterview, setStartingInterview] = useState(false);
  const [startInterviewError, setStartInterviewError] = useState('');
  const [showNoResumeModal, setShowNoResumeModal] = useState(false);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [sessionData, setSessionData] = useState(null);

  const fetchCredits = useCallback(async () => {
    try {
      setCreditLoading(true);
      const res = await api.get('observability/user-credits/');
      const data = res.data.results || res.data;
      const rawCurrentBalance = Number(data.current_balance ?? data.currentBalance);
      setCreditData({
        current_balance: Number.isFinite(rawCurrentBalance) ? rawCurrentBalance : 0,
        current_balance_expiry: data.current_balance_expiry ?? null,
        daily_credits: data.daily_credits ?? 0,
        daily_credits_expiry: data.daily_credits_expiry ?? null,
        lifetime_credits: data.lifetime_credits ?? 0,
      });
    } catch {
      setCreditData({
        current_balance: 0,
        current_balance_expiry: null,
        daily_credits: 0,
        daily_credits_expiry: null,
        lifetime_credits: 0,
      });
    } finally {
      setCreditLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  const handleInterviewStart = (sessionId, inviteToken = null) => {
    let baseInterviewUrl;
    if (process.env.REACT_APP_INTERVIEW_UI_URL) {
      baseInterviewUrl = process.env.REACT_APP_INTERVIEW_UI_URL;
    } else {
      baseInterviewUrl = window.location.origin;
    }

    const relativePath = inviteToken ? `/interview/invite/${inviteToken}` : `/interview/${sessionId}?startInterview=true`;
    const targetUrl = `${baseInterviewUrl}${relativePath}`;
    window.open(targetUrl, '_blank');
  };

  const handleStartRealtimeInterview = async () => {
    if (startingInterview) return;
    setStartingInterview(true);
    setStartInterviewError('');

    try {
      const resumeRes = await api.get('resumes/');
      const resumeList = Array.isArray(resumeRes.data?.results)
        ? resumeRes.data.results
        : (Array.isArray(resumeRes.data) ? resumeRes.data : []);

      const activeResume = resumeList.find((resume) => resume.is_active);

      if (!activeResume) {
        setShowNoResumeModal(true);
        return;
      }

      const creditResponse = await api.get('observability/user-credits/');
      const credits = getNormalizedCredits(creditResponse.data);
      if (credits <= 0) {
        setStartInterviewError(t('b2cDashboard.insufficientCredits', "You don't have enough credits. Please contact Aptiview admin to purchase credits."));
        return;
      }

      const resumeDetailRes = await api.get(`resumes/${activeResume.id}/`);
      setSelectedResume(resumeDetailRes.data || activeResume);
      setShowInterviewModal(true);
    } catch (error) {
      console.error('Failed to start real-time interview from dashboard:', error);
      setStartInterviewError(t('b2cDashboard.unableToStart', 'Unable to start interview right now. Please try again from Details.'));
    } finally {
      setStartingInterview(false);
    }
  };

  const handleViewResults = async (interview) => {
    const sessionId = interview.session_id || interview.id;
    if (!sessionId) return;

    setShowFeedbackModal(true);
    setLoadingFeedback(true);
    setFeedbackError('');
    setFeedback(null);

    try {
      const sessionRes = await api.get(`sessions/${sessionId}/`, {
        headers: { Authorization: `Token ${localStorage.getItem('adminToken')}` }
      });
      setSessionData(sessionRes.data);

      const feedbackRes = await api.get(`feedback/?session=${sessionId}`, {
        headers: { Authorization: `Token ${localStorage.getItem('adminToken')}` }
      });

      if (!feedbackRes.data || !feedbackRes.data.results || feedbackRes.data.results.length === 0) {
        if (interview.feedback) {
          setFeedback(interview.feedback);
        } else {
          setFeedbackError(t('b2cDashboard.noFeedbackFound', 'No feedback found for this session.'));
        }
        return;
      }

      setFeedback(feedbackRes.data.results[0]);
    } catch {
      if (interview.feedback) {
        setFeedback(interview.feedback);
        setSessionData({
          id: interview.id,
          started_at: interview.started_at,
          ended_at: interview.ended_at,
          duration_minutes: interview.duration_minutes,
          status: interview.status
        });
      } else {
        setFeedbackError(t('b2cDashboard.failedLoadFeedback', 'Failed to load feedback.'));
      }
    } finally {
      setLoadingFeedback(false);
    }
  };

  const avgScore = React.useMemo(() => {
    if (!candidateData?.interviews || candidateData.interviews.length === 0) return '0.00';
    const scores = candidateData.interviews.map(i => getInterviewScore(i)).filter(s => s !== null);
    if (scores.length === 0) return '0.00';
    return (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2);
  }, [candidateData]);

  const totalInterviews = candidateData?.interviewMeta?.count || candidateData?.interviews?.length || 0;

  const candidateFullName = `${candidateData?.first_name || ''} ${candidateData?.last_name || ''}`.trim() || 'Candidate';
  const candidateEmail = candidateData?.email ? `\n- Email: ${candidateData.email}` : '';
  const emailSubject = encodeURIComponent(`Request to Purchase Aptiview Credits - ${candidateFullName}`);
  const emailBody = encodeURIComponent(`Hi Aptiview Support Team,\n\nI would like to purchase additional credits to continue using the platform.\n\nCould you please provide me with the available pricing plans and the steps to complete my purchase?\n\nAccount Information:\n- Name: ${candidateFullName}${candidateEmail}\n\nThank you,\n${candidateFullName}`);

  return (
    <PageShell
      fill={false}
      style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f0f9ff 48%, #fdf4ff 100%)',
        fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <PageCard overflow={false}>
          <PageHeader
            title={t('b2cDashboard.dashboardOverview', 'Dashboard Overview')}
            subtitle={t('b2cDashboard.dashboardSubtitle', 'Track your progress, view your credits, and start practicing.')}
          />

        <div className="p-3 sm:p-4">
        {isCreditLow(creditData.current_balance) && (
          <div className="mb-4">
            <div
              className="w-full px-4 py-3 rounded-xl border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              style={{
                background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                borderColor: '#fcd34d',
                color: '#92400e',
              }}
            >
              <div className="flex items-center gap-2">
                <span style={{ fontSize: '20px' }}>⚠️</span>
                <div>
                  <h4 className="text-sm font-bold mb-0.5 text-amber-900">
                    {creditData.current_balance === 0
                      ? t('b2cDashboard.zeroCreditWarningTitle', 'Insufficient Credits')
                      : t('b2cDashboard.lowCreditWarningTitle', 'Low Credit Warning')
                    }
                  </h4>
                  <p className="text-xs text-amber-800">
                    {creditData.current_balance === 0
                      ? t('b2cDashboard.zeroCreditWarningDesc', 'Your remaining credit balance is 0. Please purchase more credits to continue practicing.')
                      : t('b2cDashboard.lowCreditWarningDesc', 'Your remaining credit balance is {{count}}. Please purchase more credits to continue practicing.', {
                        count: creditData.current_balance
                      })
                    }
                  </p>
                </div>
              </div>
              <button
                className="px-4 py-2 rounded-lg text-xs font-bold transition-all"
                style={{
                  background: '#d97706',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onClick={() => setShowBuyCreditsModal(true)}
              >
                {t('b2cDashboard.buyCredits', 'Buy Credits')}
              </button>
            </div>
          </div>
        )}

        {/* ── Hero Banner ────── */}
        <div
          className="rounded-3xl p-6 sm:p-8 md:p-10 mb-8 relative overflow-hidden border border-sky-100"
          style={{
            background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 46%, #fdf4ff 100%)',
          }}
        >
          <div className="relative z-10 md:w-2/3">
            <h1
              className="text-3xl sm:text-4xl font-extrabold mb-3"
              style={{ color: '#111827' }}
            >
              {t('b2cDashboard.welcomeBack', 'Welcome back, {{name}}! 👋', { name: candidateData?.first_name || t('b2cDashboard.welcomeBackDefault', 'Candidate') })}
            </h1>
            <p className="text-base sm:text-lg mb-8" style={{ color: '#4b5563' }}>
              {t('b2cDashboard.ctaDescription', "Let's practice and improve your interview skills today.")}
            </p>
            <div className="flex flex-wrap gap-3 sm:gap-4">
              <button
                className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-3.5 rounded-xl text-sm font-bold text-white transition-all duration-300 w-full sm:w-auto hover:-translate-y-0.5 hover:brightness-105"
                style={{
                  background: THEME_GRADIENT,
                  boxShadow: THEME_BUTTON_SHADOW,
                  border: 'none',
                  cursor: 'pointer',
                }}
                onClick={handleStartRealtimeInterview}
                disabled={startingInterview}
              >
                <PlayArrowIcon style={{ fontSize: 20 }} />
                {startingInterview ? t('b2cDashboard.starting', 'Starting...') : t('b2cDashboard.startMockInterview', 'Start Mock Interview')}
              </button>

              <button
                className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-3.5 rounded-xl text-sm font-bold transition-all duration-300 bg-white w-full sm:w-auto"
                style={{
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                }}
                onClick={() => navigate('/publicjobdescriptions')}
              >
                <SearchIcon style={{ fontSize: 20 }} />
                {t('b2cDashboard.searchJobs', 'Search Jobs')}
              </button>

              <button
                className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-3.5 rounded-xl text-sm font-bold transition-all duration-300 bg-white w-full sm:w-auto"
                style={{
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                }}
                onClick={() => navigate('/resume-analyze')}
              >
                <DescriptionIcon style={{ fontSize: 20 }} />
                {t('b2cDashboard.analyzeResume', 'Analyze Resume')}
              </button>
            </div>
            {startInterviewError && (
              <div
                className="mt-4 rounded-lg border px-4 py-3 text-sm font-medium"
                style={{
                  backgroundColor: '#fef2f2',
                  borderColor: '#fecaca',
                  color: '#b91c1c',
                  maxWidth: 'fit-content'
                }}
              >
                {startInterviewError}
              </div>
            )}
          </div>

          {/* Target Illustration */}
          <div className="hidden md:block absolute right-8 lg:right-16 top-1/2 -translate-y-1/2 pointer-events-none">
            <img src={targetImg} alt="Target Illustration" className="w-56 lg:w-64 h-auto" style={{ mixBlendMode: 'multiply', WebkitMaskImage: 'radial-gradient(circle closest-side, black 80%, transparent 100%)', maskImage: 'radial-gradient(circle closest-side, black 80%, transparent 100%)' }} />
            {/* Sparkles */}
            <div className="absolute top-4 left-0 w-3 h-3 bg-white rounded-full opacity-60 shadow-[0_0_10px_rgba(255,255,255,0.8)]"></div>
            <div className="absolute bottom-10 right-4 w-4 h-4 bg-white rounded-full opacity-70 shadow-[0_0_10px_rgba(255,255,255,0.8)]"></div>
          </div>
        </div>

        {/* ── Summary Cards ─────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3.5 sm:gap-4 lg:gap-5 mb-8">
          {/* Credits Available */}
          <div
            className={`rounded-2xl p-4 sm:p-5 border transition-all duration-200 h-full flex flex-col justify-between ${
              isCreditLow(creditData.current_balance)
                ? 'border-amber-300 bg-amber-50/40 shadow-[0_2px_10px_rgba(217,119,6,0.06)] hover:shadow-[0_8px_20px_rgba(217,119,6,0.12)]'
                : 'bg-white border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.02)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.06)] hover:border-gray-200'
            }`}
          >
            <div>
              <div className="flex items-start gap-3">
                <div
                  className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shrink-0"
                  style={
                    isCreditLow(creditData.current_balance)
                      ? { background: '#fef3c7', color: '#d97706' }
                      : { background: '#f3e8ff', color: '#9333ea' }
                  }
                >
                  <AccountBalanceWalletIcon style={{ fontSize: 22 }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-gray-500 mb-1 min-h-[2.25rem] flex items-center flex-wrap gap-1 leading-snug">
                    <span className="line-clamp-2" title={t('b2cDashboard.creditsAvailable', 'Credits Available')}>
                      {t('b2cDashboard.creditsAvailable', 'Credits Available')}
                    </span>
                    {isCreditLow(creditData.current_balance) && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 animate-pulse shrink-0">
                        {t('b2cDashboard.lowBalanceBadge', 'LOW')}
                      </span>
                    )}
                  </div>
                  <div className={`text-2xl sm:text-3xl font-extrabold tracking-tight ${isCreditLow(creditData.current_balance) ? 'text-amber-800' : 'text-gray-900'}`}>
                    {creditData.current_balance ?? 0}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100/80">
              <button 
                onClick={() => setShowBuyCreditsModal(true)}
                className="text-xs sm:text-sm font-semibold flex items-center gap-1 group buy-credits-btn transition-colors" 
                style={{ color: isCreditLow(creditData.current_balance) ? '#d97706' : '#0284c7' }}
              >
                {t('b2cDashboard.buyCredits', 'Buy Credits')} <ChevronRightIcon fontSize="small" className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Interviews Taken */}
          <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.02)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.06)] hover:border-gray-200 transition-all duration-200 h-full flex flex-col justify-between">
            <div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#eff6ff', color: '#3b82f6' }}>
                  <AssessmentIcon style={{ fontSize: 22 }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-gray-500 mb-1 min-h-[2.25rem] flex items-center leading-snug">
                    <span className="line-clamp-2" title={t('b2cDashboard.interviewsTaken', 'Interviews Taken')}>
                      {t('b2cDashboard.interviewsTaken', 'Interviews Taken')}
                    </span>
                  </div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
                    {totalInterviews}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100/80">
              <button onClick={() => navigate('/progress')} className="text-xs sm:text-sm font-semibold flex items-center gap-1 group transition-colors" style={{ color: '#3b82f6' }}>
                {t('b2cDashboard.viewHistory', 'View History')} <ChevronRightIcon fontSize="small" className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* AI Feedback Score */}
          <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.02)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.06)] hover:border-gray-200 transition-all duration-200 h-full flex flex-col justify-between">
            <div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#fdf4ff', color: '#d946ef' }}>
                  <EmojiEventsIcon style={{ fontSize: 22 }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-gray-500 mb-1 min-h-[2.25rem] flex items-center leading-snug">
                    <span className="line-clamp-2" title={t('b2cDashboard.aiFeedbackAvgScore', 'AI Feedback Avg Score')}>
                      {t('b2cDashboard.aiFeedbackAvgScore', 'AI Feedback Avg Score')}
                    </span>
                  </div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
                    {avgScore}%
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100/80">
              <button onClick={() => navigate('/progress')} className="text-xs sm:text-sm font-semibold flex items-center gap-1 group transition-colors" style={{ color: '#d946ef' }}>
                {t('b2cDashboard.viewAnalytics', 'View Analytics')} <ChevronRightIcon fontSize="small" className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Lifetime Credits */}
          <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.02)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.06)] hover:border-gray-200 transition-all duration-200 h-full flex flex-col justify-between">
            <div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#f0fdf4', color: '#22c55e' }}>
                  <AllInclusiveIcon style={{ fontSize: 22 }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-gray-500 mb-1 min-h-[2.25rem] flex items-center leading-snug">
                    <span className="line-clamp-2" title={t('b2cDashboard.lifetimeCredits', 'Lifetime Credits')}>
                      {t('b2cDashboard.lifetimeCredits', 'Lifetime Credits')}
                    </span>
                  </div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
                    {creditData.lifetime_credits}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100/80">
              <button onClick={() => setShowLedgerModal(true)} className="text-xs sm:text-sm font-semibold flex items-center gap-1 group transition-colors" style={{ color: '#22c55e' }}>
                View Ledger <ChevronRightIcon fontSize="small" className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Expiries */}
          <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.02)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.06)] hover:border-gray-200 transition-all duration-200 h-full flex flex-col justify-between sm:col-span-2 lg:col-span-2 xl:col-span-1">
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-3 mb-1">
                {/* Daily Expiry */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#fffbeb', color: '#f59e0b' }}>
                    <ScheduleIcon style={{ fontSize: 22 }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-gray-500 mb-1 min-h-[2.25rem] flex items-center leading-snug">
                      <span className="line-clamp-2" title={t('b2cDashboard.dailyExpiry', 'Daily Expiry')}>
                        {t('b2cDashboard.dailyExpiry', 'Daily Expiry')}
                      </span>
                    </div>
                    <div className="text-xl sm:text-2xl font-extrabold text-gray-900 tracking-tight leading-tight">
                      <CountdownDisplay dateStr={creditData.daily_credits_expiry} compact />
                    </div>
                  </div>
                </div>

                {/* Balance Expiry */}
                <div className="flex items-center justify-between bg-amber-50/60 rounded-xl px-3 py-2 text-xs border border-amber-100/80 sm:h-auto self-center">
                  <span className="text-amber-900/80 font-medium truncate mr-2">
                    {t('b2cDashboard.balanceExpiry', 'Balance Expiry')}:
                  </span>
                  <span className="font-bold text-amber-950 shrink-0">
                    {creditData.current_balance_expiry ? formatExpiry(creditData.current_balance_expiry, i18n?.language) : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-gray-100/80">
              <button onClick={() => setShowLedgerModal(true)} className="text-xs sm:text-sm font-semibold flex items-center gap-1 group text-amber-600 hover:text-amber-700 transition-colors">
                View Ledger <ChevronRightIcon fontSize="small" className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>

        {/* ── 3 Column Layout ─────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Quick Access */}
          <div className="bg-white rounded-3xl p-7 border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.02)]">
            <h3 className="text-lg font-extrabold text-gray-900 mb-5">{t('b2cDashboard.quickAccess', 'Quick Access')}</h3>
            <div className="flex flex-col space-y-2">
              {[
                { icon: <MicIcon style={{ color: '#8b5cf6', fontSize: 20 }} />, bg: '#f5f3ff', title: t('b2cDashboard.practiceInterview', 'Practice Interview'), sub: t('b2cDashboard.realTimeAiMock', 'Real-time AI mock interview'), onClick: handleStartRealtimeInterview },
                { icon: <SearchIcon style={{ color: '#0ea5e9', fontSize: 20 }} />, bg: '#f0f9ff', title: t('b2cDashboard.jobSearch', 'Job Search'), sub: t('b2cDashboard.findJobsFilters', 'Find jobs by filters'), onClick: () => navigate('/publicjobdescriptions') },
                { icon: <DescriptionIcon style={{ color: '#06b6d4', fontSize: 20 }} />, bg: '#ecfeff', title: t('b2cDashboard.resumeAnalyze', 'Resume Analyze'), sub: t('b2cDashboard.getAiFeedback', 'Get AI feedback & score'), onClick: () => navigate('/resume-analyze') },
                { icon: <AutoGraphIcon style={{ color: '#d946ef', fontSize: 20 }} />, bg: '#fdf4ff', title: t('b2cDashboard.myProgress', 'My Progress'), sub: t('b2cDashboard.trackImprovement', 'Track your improvement'), onClick: () => navigate('/progress') }
              ].map((item, idx) => (
                <div key={idx} onClick={item.onClick} className="flex items-center justify-between p-3 rounded-2xl hover:bg-gray-50 cursor-pointer transition-colors group">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: item.bg }}>{item.icon}</div>
                    <div>
                      <div className="text-sm font-bold text-gray-900 mb-0.5">{item.title}</div>
                      <div className="text-xs text-gray-500">{item.sub}</div>
                    </div>
                  </div>
                  <ChevronRightIcon className="text-gray-400 group-hover:text-gray-900 transition-colors" />
                </div>
              ))}
            </div>
          </div>

          {/* Recent Interviews */}
          <div className="lg:col-span-2 bg-white rounded-3xl p-7 border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.02)]">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-extrabold text-gray-900">{t('b2cDashboard.recentInterviews', 'Recent Interviews')}</h3>
              <button onClick={() => navigate('/progress')} className="text-sm font-bold hover:underline" style={{ color: '#0284c7' }}>{t('b2cDashboard.viewAllHistory', 'View all history')}</button>
            </div>
            <div className="flex flex-col space-y-4">
              {candidateData?.interviews && candidateData.interviews.length > 0 ? (
                candidateData.interviews.slice(0, 5).map((interview, idx) => {
                  const score = getInterviewScore(interview);
                  const status = getTranslatedStatus(interview.status, t);
                  const date = formatExpiry(interview.started_at || interview.created_at);
                  // duration available via formatInterviewDuration(interview)

                  const jobTitle = interview.job_description?.title || t('b2cDashboard.practiceInterview', 'Practice Interview');
                  const candidateName = candidateData?.first_name
                    ? `${candidateData.first_name} ${candidateData.last_name || ''}`.trim()
                    : t('b2cDashboard.welcomeBackDefault', 'Candidate');

                  const scoreValue = score !== null ? score : 0;
                  const scoreColor = scoreValue >= 70 ? '#10b981' : scoreValue >= 40 ? '#f59e0b' : '#ef4444';

                  const langCode = (interview.language || 'en').toLowerCase();
                  const langName = t(`languages.${langCode}`, langCode.toUpperCase());

                  return (
                    <div key={idx} className="flex flex-col p-4 rounded-xl border border-gray-100 hover:border-indigo-100 transition-colors shadow-sm gap-2 bg-white">
                      
                      {/* Columns Row */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3">
                        {/* Name */}
                        <div className="flex items-center gap-4 flex-1 min-w-[120px]">
                          <div>
                            <div className="text-base font-bold text-gray-900 leading-tight mb-1">{candidateName}</div>
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              <EventAvailableIcon style={{ fontSize: 14, color: '#10b981' }} />
                              <span>{date}</span>
                            </div>
                          </div>
                        </div>

                        {/* Role & Experience */}
                        <div className="flex-1 hidden md:block min-w-[120px]">
                          <div className="flex items-center gap-1.5 text-gray-700 font-semibold text-sm mb-1 truncate">
                            <WorkOutlineIcon style={{ fontSize: 16, color: '#9ca3af' }} />
                            {jobTitle}
                          </div>
                          <div className="text-xs text-gray-500 ml-5">
                            {t('b2cDashboard.entry', 'entry')}
                          </div>
                        </div>

                        {/* Resume View */}
                        <div className="flex-none hidden xl:block">
                          <div
                            className="flex items-center gap-1.5 text-blue-600 font-semibold text-sm mb-1 cursor-pointer hover:underline"
                            onClick={() => {
                              const url = interview?.resume?.file || interview?.resume?.file_url || interview?.resume_url;
                              if (url) window.open(url, '_blank');
                            }}
                          >
                            <DescriptionIcon style={{ fontSize: 16 }} />
                            {t('b2cDashboard.view', 'View')}
                          </div>
                          <div className="text-xs text-gray-500 ml-5">
                            {date}
                          </div>
                        </div>

                        {/* Language */}
                        <div className="flex-none hidden 2xl:block">
                          <div className="flex items-center gap-1.5 text-gray-700 font-semibold text-sm mb-1">
                            <TranslateIcon style={{ fontSize: 16, color: '#9ca3af' }} />
                            {langName} ({langCode.toUpperCase()})
                          </div>
                          <div className="text-xs text-gray-500 ml-5">
                            {langName}
                          </div>
                        </div>

                        {/* Status */}
                        <div className="flex-none flex items-center justify-start sm:justify-center xl:justify-start">
                          <div className={`px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${interview.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {interview.status === 'completed' && <CheckCircleIcon style={{ fontSize: 14 }} />}
                            {status}
                          </div>
                        </div>

                        {/* Feedback */}
                        <div className="flex-none flex items-center justify-start sm:justify-center xl:justify-start">
                          {interview.status === 'completed' ? (
                            <button
                              className="w-10 h-10 rounded-xl bg-blue-600 hover:bg-blue-700 active:scale-95 text-white shadow-sm hover:shadow-md transition-all cursor-pointer border-none flex items-center justify-center shrink-0"
                              onClick={() => handleViewResults(interview)}
                              title={t('b2cDashboard.viewFeedback', 'View Feedback')}
                              aria-label={t('b2cDashboard.viewFeedback', 'View Feedback')}
                            >
                              <VisibilityIcon style={{ fontSize: 20, color: '#fff' }} />
                            </button>
                          ) : (
                            <div
                              className="w-10 h-10 rounded-xl bg-gray-100 text-gray-400 cursor-not-allowed flex items-center justify-center shrink-0"
                              title={t('b2cDashboard.noFeedback', 'N/A')}
                            >
                              <VisibilityIcon style={{ fontSize: 20 }} />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Score Row */}
                      <div className="flex items-center gap-1 mt-2 sm:mt-1 self-start whitespace-nowrap">
                        <TrendingUpIcon style={{ fontSize: 18, color: scoreColor }} />
                        <span className="text-base font-extrabold" style={{ color: scoreColor }}>{scoreValue.toFixed(2)}%</span>
                        <span className="text-sm font-semibold text-gray-500">/ 100.00%</span>
                      </div>

                    </div>
                  );
                })
              ) : (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-3 text-gray-400">
                    <AssessmentIcon style={{ fontSize: 32 }} />
                  </div>
                  <h4 className="text-base font-bold text-gray-900 mb-1">{t('b2cDashboard.noInterviewsYet', 'No interviews yet')}</h4>
                  <p className="text-sm text-gray-500 mb-4">{t('b2cDashboard.startFirstInterviewHint', 'Start your first mock interview to see your history here.')}</p>
                  <button
                    onClick={handleStartRealtimeInterview}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-300"
                    style={{ background: THEME_GRADIENT, boxShadow: THEME_BUTTON_SHADOW }}
                  >
                    <PlayArrowIcon style={{ fontSize: 18 }} />
                    {t('b2cDashboard.startInterview', 'Start Interview')}
                  </button>
                </div>
              )}
            </div>
          </div>

        </div>
        </div>
      </PageCard>

      {/* ── Transaction History Modal ──────── */}
      {showLedgerModal && (
        <div className="fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white shadow-xl border border-gray-200 flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">{t('b2cDashboard.creditLedger', 'Credit Ledger')}</h3>
              <button
                onClick={() => setShowLedgerModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-4 overflow-y-auto">
              <TransactionHistory />
            </div>
          </div>
        </div>
      )}

      <B2CInterviewSetupModal
        isOpen={showInterviewModal}
        onClose={() => setShowInterviewModal(false)}
        resume={selectedResume}
        onStartInterview={handleInterviewStart}
        creditBalance={creditData.current_balance}
      />

      {showNoResumeModal && (
        <div className="fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl border border-gray-100 p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('b2cDashboard.noResumeFound', 'No Resume Found')}</h3>
            <p className="text-sm text-gray-600 mb-6">
              {t('b2cDashboard.noResumeDescriptionCandidateDashboard', 'You need to upload a resume before starting an interview. Please upload one in your details page.')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowNoResumeModal(false)}
                className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-bold hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={() => {
                  setShowNoResumeModal(false);
                  navigate('/details');
                }}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-300"
                style={{
                  background: THEME_GRADIENT,
                  boxShadow: THEME_BUTTON_SHADOW,
                }}
              >
                <PlayArrowIcon style={{ fontSize: 18 }} />
                {t('b2cDashboard.goToDetails', 'Go to Details')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBuyCreditsModal && (
        <div className="fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl border border-gray-100 p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('b2cDashboard.buyCreditsTitle', 'Purchase Credits')}</h3>
            <p className="text-sm text-gray-600 mb-6">
              {t('b2cDashboard.buyCreditsDesc', 'Hi {{name}}, to purchase additional credits, please send an email to info@aptiview.ai with your request.', { name: candidateData?.first_name || 'Candidate' })}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowBuyCreditsModal(false)}
                className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-bold hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <a
                href={`https://mail.google.com/mail/?view=cm&fs=1&to=info@aptiview.ai&su=${emailSubject}&body=${emailBody}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowBuyCreditsModal(false)}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-300 no-underline"
                style={{
                  background: THEME_GRADIENT,
                  boxShadow: THEME_BUTTON_SHADOW,
                }}
              >
                {t('b2cDashboard.sendEmail', 'Send Email')}
              </a>
            </div>
          </div>
        </div>
      )}

      {showFeedbackModal && (
        <ViewResult
          feedback={feedback}
          loadingFeedback={loadingFeedback}
          feedbackError={feedbackError}
          onClose={() => setShowFeedbackModal(false)}
          sessionData={sessionData}
          isSendingEmail=""
          emailSent=""
          sendEmail=""
          callFrom="interviewSection"
        />
      )}
    </PageShell>
  );
}
